import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './SubscriptionOptions.css';
import { postRequest } from '../../api';
import { getPublicPlans } from '../../api/Stripe';
import { notification, Col, Input, Row, Spin } from "antd";
import { validateCoupon } from '../../api/password';

const SubscriptionOption = ({ title, desc, pkg, price, extra, details, detail, isSelected, onClick }) => (
  <div
    className={`subscription-option ${isSelected ? "selected" : ""}`}
    onClick={onClick}
  >
    <Row
      className="heading-description-container"
    >
      <h2 className="text-align-center" style={{ display: "block" }}>{title}</h2>
      <div className="desc">
        {desc.map((desc, index) => (
          <p className="lh-24" key={index}>{desc}</p>
        ))}
      </div>
    </Row>
    <Row
      className="price-container"
    >
      <div className="plan">
        <p className="pkg">{pkg}</p>
        <span className="price">&nbsp;{price}</span>
      </div>

      <div className="text-align-center">
        <span className="extra">
          {extra}
        </span>
      </div>

      <div>
        <ul className="details">
          {details.map((details, index) => (
            <li key={index}>{details}</li>
          ))}
        </ul>
      </div>
    </Row>
    <button className={`confirm-button ${isSelected ? "confirmed" : ""}`}>
      {isSelected ? "Selected" : "Confirm"}
    </button>
    <Row style={{ height: "auto" }}>
      <div className="detail">
        {detail.map((detail, index) => (
          <p className="lh-18" key={index}>{detail}</p>
        ))}
      </div>
    </Row>
  </div>
);

const SubscriptionOptions = () => {
  const [selectedOption, setSelectedOption] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();
  const { city, country, address, postalCode } = location.state || {};
  const [couponCode, setCouponCode] = useState('');

  useEffect(() => {
    const createCustomer = async () => {
      try {
        const customerData = {
          city,
          country
        };

        if (address !== 'Unknown') {
          customerData.address = { line1: address };
        }

        if (postalCode !== 'Unknown') {
          customerData.address = { ...customerData.address, postal_code: postalCode };
        }

        await postRequest('stripe/create-customer', customerData);

      } catch (error) {
        console.error('Customer Creation Error', error.message);
      }
    };

    const fetchPlans = async () => {
      try {
        setLoading(true);
        const response = await getPublicPlans();
        if (response && response.data && response.data.subscriptionPlans) {
          setPlans(response.data.subscriptionPlans);
        }
      } catch (error) {
        console.error('Error fetching plans:', error);
      } finally {
        setLoading(false);
      }
    };

    createCustomer();
    fetchPlans();
  }, [city, country, address, postalCode]);

  const handleConfirm = async (option) => {
    localStorage.setItem('free', option.title)
    setSelectedOption(option);
    if (option.title === "One Week Free Trial") {
      setCouponCode('');
    }
  };

  const handleNext = async () => {
    if (!selectedOption) {
      notification.warning({
        message: 'Selection Required',
        description: 'Please select a subscription option.',
        duration: 10
      });
      return;
    }
    if (selectedOption.title === "One Week Free Trial") {
      const planId = process.env.REACT_APP_FREE_PLAN;
      if (planId) {
        try {
          const response = await postRequest('/stripe/create-free-subscription', {
            planId: planId,
          });
          if (response) {
            navigate('/onboarding/get-to-know-you');
          } else {
            notification.error({
              message: 'Subscription Error',
              description: 'Failed to create subscription. Please try again.',
              duration: 10
            });
          }
        } catch (error) {
          console.error('Error creating free trial subscription:', error);
          notification.error({
            message: 'Subscription Error',
            description: 'An error occurred. Please try again.',
            duration: 10
          });
        }
        return;
      } else {
        notification.error({
          message: 'Configuration Error',
          description: 'User ID or Plan ID is missing.',
          duration: 10
        });
        return;
      }
    }
    let couponResponse = null;
    if (couponCode) {
      try {
        const response = await validateCoupon({ discountCode: couponCode });
        if (response.error) {
          notification.error({
            message: 'Coupon Validation Error',
            description: response.error,
            duration: 10
          });
          return;
        } else {
          notification.success({
            message: 'Coupon Validated',
            description: response.message,
            duration: 10
          });
          couponResponse = response;
        }
      } catch (error) {
        notification.error({
          message: 'Coupon Validation Error',
          description: error.message,
          duration: 10
        });
        return;
      }
    }
    navigate('/confirm-subs', { state: { selectedOption, couponResponse } });
  };

  const monthlyPlan = plans.find(p => p.interval === 'month');
  const annualPlan = plans.find(p => p.interval === 'year');

  const options = [
    {
      title: "One Week Free Trial",
      pkg: "FREE",
      price: "/one week",
      extra: "",
      desc: ["Trial our basic features free of charge, without having to provide your payment details."],
      details: ["5 minute limited conversations with My Friend", "3 quick links permitted"],
      buttonText: "Confirm",
      detail: [`*After the first week, you can choose to be moved onto the £${monthlyPlan?.price?.toFixed(2) || '7.29'} monthly subscription. The monthly price is subject to change.`]
    },
    {
      title: "Pay Monthly",
      pkg: `£${monthlyPlan?.price?.toFixed(2) || '7.29'}`,
      price: "/month",
      extra: "+ 30p transaction charge",
      desc: ["Access to all our features for free for the first month, then:"],
      details: [],
      buttonText: "Confirm",
      detail: ["*Your subscription will automatically renew, but you can cancel at any time. You must give one month's notice before your subscription is cancelled. The monthly price is subject to change."]
    },
    {
      title: "Pay Annually",
      pkg: `£${annualPlan?.price?.toFixed(2) || '69.90'}`,
      price: "/year",
      extra: "",
      desc: ["Get 12 months for the price of 10.", "No transaction fee."],
      details: ["No transaction fee."],
      buttonText: "Confirm",
      detail: ["*Cancel at any time, but you must have given one month's notice before your subscription is cancelled. The annual price is subject to change."]
    }
  ];

  if (loading) {
    return (
      <Row className="welcome-onboarding-row" style={{ minHeight: '400px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Spin size="large" />
        <p style={{ marginTop: '20px', textAlign: 'center', width: '100%' }}>Loading subscription plans...</p>
      </Row>
    );
  }

  return (
    <Row className="welcome-onboarding-row">
      <Row
        gutter={24}
        className="chose-subscription-text-container"
      >
        <Col span={24}>
          <span
            className="heading text-align-center"
            style={{ display: "block" }}
          >
            Choose Your Subscription
          </span>
        </Col>
      </Row>
      <Row
        gutter={24}
        className="chose-subscription-text-container"
      >
        <Col className="flex-justify-center">
          <span
            className="text chose-subscription-description-text"
          >
            Please select the payment option that best suits your needs. All
            options will take you directly to the secure payment provider
            platform. We have selected to use Stripe as our provider to ensure
            secure and efficient online protection of your data.
          </span>
        </Col>
      </Row>
      <div className="subscription-options-container">
        <Row
          gutter={8}
          className="justify-content-center flex-justify-space-center"
        >
          {options.map((option, index) => (
            <Col key={index} xs={24} sm={24} md={8} lg={7}>
              <SubscriptionOption
                {...option}
                isSelected={selectedOption?.title === option.title}
                onClick={() => handleConfirm(option)}
                onConfirm={() => handleConfirm(option)}
              />
            </Col>
          ))}
        </Row>
        <Row className="coupon-code">
          <Col span={24}>
            <Input
              placeholder="Enter Coupon Code (Optional)"
              value={couponCode}
              className='coupon-code-input'
              disabled={selectedOption?.title === "One Week Free Trial"}
              onChange={(e) => setCouponCode(e.target.value)}
            />
          </Col>
        </Row>
        <Row className="flex-justify-space-around">
          <button className="next-button-1" onClick={handleNext}>
            Next
          </button>
        </Row>
      </div>
    </Row>
  );
};
export default SubscriptionOptions;